/*
 * supps-free-bag.js -- free first-delivery gift bag as a real cart line.
 * 2-pack and 3-pack subscribers get 1 free bag with their first delivery.
 * (3-pack's second free bag, Month 2, is added post-purchase by the
 * orders/create webhook -- app/api_v2/routes_webhooks.py -- untouched here.)
 *
 * 2026-08-17: the bag is now BUNDLED INTO THE ADD ITSELF.
 *
 * Until today this script only reconciled AFTER a cart mutation: it watched
 * fetch, then issued /cart.js + /cart/add.js of its own. Measured on the live
 * site, the bag did not exist in the cart until +1410ms after the shopper's
 * click -- so the cart drawer, the cart page and the checkout snapshot all
 * showed no bag for a second and a half. Any shopper who hit checkout inside
 * that window bought without one. That is why only 6 of 22 qualifying orders
 * carried the line: a race the shopper often won.
 *
 * Now, when a qualifying subscription line is added, the free bag is appended
 * to the SAME /cart/add.js request as a second item. The cart is correct the
 * instant the request returns, so the drawer renders it on first paint. There
 * is no window to lose.
 *
 * The post-hoc reconcile below is KEPT, but demoted to a safety net for the
 * paths that are not an add: quantity changes, removals, /cart/update,
 * /cart/clear, and full-page navigations.
 */
(function () {
  'use strict';

  if (window.SuppsFreeBag) return;

  var FREE_BAG_VARIANT_ID = 54978879390025;
  var QUALIFYING_VARIANT_IDS = []; // sales slow-down stage 1: no free bags on new orders // Berry Blast 2-pack / 3-pack
  var CART_MUTATION_PATHS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];

  var reconciling = false;
  var pending = false;

  // Last known truth about whether the cart already holds a bag, refreshed from
  // every cart payload this script sees. Consulted SYNCHRONOUSLY when deciding
  // whether to bundle, so the decision costs no round trip. If it is ever
  // wrong, reconcile() still trims the cart back to exactly one bag.
  var cartHasBag = false;

  function noteCart(cart) {
    if (cart && cart.items) {
      cartHasBag = cart.items.some(function (item) {
        return item.variant_id === FREE_BAG_VARIANT_ID;
      });
    }
    return cart;
  }

  function isCartMutationUrl(input) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var path = url.split('?')[0];
      return CART_MUTATION_PATHS.some(function (p) {
        return path.indexOf(p) !== -1;
      });
    } catch (e) {
      return false;
    }
  }

  function isAddUrl(input) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      return url.split('?')[0].indexOf('/cart/add') !== -1;
    } catch (e) {
      return false;
    }
  }

  function isFullCartPage() {
    return !!document.getElementById('cart');
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(noteCart);
  }

  function cartQualifies(cart) {
    return cart.items.some(function (item) {
      return QUALIFYING_VARIANT_IDS.indexOf(item.variant_id) !== -1 && !!item.selling_plan_allocation;
    });
  }

  function freeBagLines(cart) {
    return cart.items.filter(function (item) {
      return item.variant_id === FREE_BAG_VARIANT_ID;
    });
  }

  function postJSON(url, payload) {
    payload.sections = 'cart-drawer,cart-icon-bubble';
    payload.sections_url = window.location.pathname;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (r) {
      return r.json();
    });
  }

  function cartAdd(variantId, quantity) {
    return postJSON('/cart/add.js', { id: variantId, quantity: quantity });
  }

  function cartChange(key, quantity) {
    return postJSON('/cart/change.js', { id: key, quantity: quantity });
  }

  /*
   * Read an add-to-cart body regardless of how the caller built it.
   *
   * This matters more than it looks: the live product page uses Dawn's
   * <product-form>, which posts a FormData, NOT JSON. An earlier draft of this
   * fix only understood JSON bodies, so against the real Add to cart button it
   * would have silently done nothing while passing a synthetic JSON test.
   * Handle all three shapes the theme and its app extensions can produce.
   */
  function readAddBody(body) {
    try {
      if (typeof body === 'string') {
        // Either JSON or url-encoded.
        if (body.charAt(0) === '{') return { kind: 'json', data: JSON.parse(body) };
        var params = new URLSearchParams(body);
        var fromParams = {};
        params.forEach(function (v, k) {
          fromParams[k] = v;
        });
        return { kind: 'urlencoded', data: fromParams };
      }
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        var fromForm = {};
        body.forEach(function (v, k) {
          fromForm[k] = v;
        });
        return { kind: 'formdata', data: fromForm };
      }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        var fromUsp = {};
        body.forEach(function (v, k) {
          fromUsp[k] = v;
        });
        return { kind: 'urlencoded', data: fromUsp };
      }
    } catch (e) {
      /* fall through -- an unreadable body just means we do not bundle */
    }
    return null;
  }

  // Should this add carry a free bag with it?
  function bundleTarget(data) {
    if (!data || data.items) return null; // already a multi-item add; leave it alone
    var id = Number(data.id);
    if (QUALIFYING_VARIANT_IDS.indexOf(id) === -1) return null;
    if (!data.selling_plan) return null; // one-time purchase gets no bag
    if (cartHasBag) return null; // already has one; do not create a duplicate
    return id;
  }

  function buildBundledInit(init, data, variantId) {
    var first = { id: variantId, quantity: Number(data.quantity) || 1, selling_plan: data.selling_plan };

    // Carry any line item properties through onto the purchased line.
    Object.keys(data).forEach(function (k) {
      if (k.indexOf('properties[') === 0) {
        first.properties = first.properties || {};
        first.properties[k.slice(11, -1)] = data[k];
      }
    });

    var payload = {
      items: [first, { id: FREE_BAG_VARIANT_ID, quantity: 1 }],
      sections: data.sections || 'cart-drawer,cart-icon-bubble',
      sections_url: data.sections_url || window.location.pathname,
    };

    var headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    return Object.assign({}, init, { body: JSON.stringify(payload), headers: headers });
  }

  function applySections(sections) {
    if (!sections) return;
    if (sections['cart-drawer']) {
      var parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      ['cart-drawer-items', '.cart-drawer__footer'].forEach(function (selector) {
        var target = document.querySelector(selector);
        var source = parsed.querySelector(selector);
        if (target && source) target.replaceWith(source);
      });
    }
    if (sections['cart-icon-bubble']) {
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        var parsedBubble = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        var src = parsedBubble.getElementById('cart-icon-bubble') || parsedBubble.querySelector('#cart-icon-bubble');
        if (src) bubble.innerHTML = src.innerHTML;
      }
    }
  }

  function reconcile() {
    if (reconciling) {
      pending = true;
      return;
    }
    reconciling = true;

    fetchCart()
      .then(function (cart) {
        var expected = cartQualifies(cart) ? 1 : 0;
        var lines = freeBagLines(cart);
        var actual = lines.reduce(function (sum, l) {
          return sum + l.quantity;
        }, 0);

        if (actual === expected) return null;

        var chain = Promise.resolve();
        var lastResponse = null;

        // Always clear existing free-bag lines first, then re-add the
        // correct quantity. Handles missing/extra/duplicate-line cases
        // with one code path instead of branching on every combination.
        lines.forEach(function (l) {
          chain = chain.then(function () {
            return cartChange(l.key, 0).then(function (res) {
              lastResponse = res;
            });
          });
        });

        if (expected === 1) {
          chain = chain.then(function () {
            return cartAdd(FREE_BAG_VARIANT_ID, 1).then(function (res) {
              lastResponse = res;
            });
          });
        }

        return chain.then(function () {
          cartHasBag = expected === 1;
          return lastResponse;
        });
      })
      .then(function (response) {
        reconciling = false;
        if (response) {
          if (isFullCartPage()) {
            window.location.reload();
            return;
          }
          applySections(response.sections);
        }
        if (pending) {
          pending = false;
          reconcile();
        }
      })
      .catch(function () {
        reconciling = false;
        if (pending) {
          pending = false;
          reconcile();
        }
      });
  }

  var originalFetch = window.fetch;
  window.fetch = function (input, init) {
    // 1. Bundle the bag into a qualifying add, so the cart is never briefly wrong.
    //
    // Deliberately NOT guarded by `reconciling`. It was, until a test caught the
    // hole: reconcile() runs on page load, and an add fired while it is still in
    // flight would skip bundling and drop back to the old racy path -- hitting
    // exactly the fast shopper this fix exists for. The guard was never needed
    // here anyway: this script's own adds are the gift variant with no selling
    // plan, and bundleTarget() requires a qualifying variant WITH one, so an
    // internal add can never match and can never bundle itself recursively.
    if (isAddUrl(input) && init && init.body) {
      var parsed = readAddBody(init.body);
      var variantId = parsed && bundleTarget(parsed.data);
      if (variantId) {
        var self = this;
        var args = arguments;
        var bundled = buildBundledInit(init, parsed.data, variantId);
        cartHasBag = true; // optimistic; reconcile corrects it if the add fails
        return originalFetch
          .call(self, input, bundled)
          .then(function (response) {
            if (response && response.ok) return response;
            // Bundled add refused (e.g. the gift variant became unavailable).
            // Fall back to the shopper's original request so the product still
            // reaches the cart -- degrade to the old behaviour, never to a
            // broken Add to cart button.
            cartHasBag = false;
            return originalFetch.apply(self, args).then(function (plain) {
              setTimeout(reconcile, 0);
              return plain;
            });
          })
          .catch(function () {
            cartHasBag = false;
            return originalFetch.apply(self, args).then(function (plain) {
              setTimeout(reconcile, 0);
              return plain;
            });
          });
      }
    }

    // 2. Everything else: reconcile afterwards, as before.
    var promise = originalFetch.apply(this, arguments);
    if (!reconciling && isCartMutationUrl(input)) {
      // Listen on a derived promise, and swallow ITS rejection. Without the
      // catch, a failed cart fetch rejects a promise nobody handles and the
      // browser reports an unhandled rejection for an error the caller is
      // already dealing with. The ORIGINAL promise is still what we return,
      // so the caller's own error handling is untouched.
      promise
        .then(function (response) {
          if (response && response.ok) setTimeout(reconcile, 0);
        })
        .catch(function () {
          /* the caller owns this failure; we only listened so we could reconcile */
        });
    }
    return promise;
  };

  /*
   * XMLHttpRequest parity.
   *
   * The patch above only sees fetch. Dawn's <product-form> uses fetch, but app
   * embeds and older theme code can still add to cart over XHR, and those adds
   * were bypassing this script completely: no bag bundled, and not even a
   * reconcile until the shopper's next page load, so they could reach checkout
   * without the gift.
   *
   * Deliberately NOT bundling here. Rewriting an in-flight XHR body cannot be
   * cleanly retried the way the fetch path retries it, so a refused bundled add
   * would leave the shopper's product out of the cart altogether. A broken Add
   * to cart is a lost sale; a bag that arrives a moment late is not. So the
   * shopper's request goes out untouched and the bag follows on success. That
   * is the old racy path, which for XHR adds specifically is the right trade,
   * and it is strictly better than the current behaviour of doing nothing.
   */
  var XHR = window.XMLHttpRequest;
  if (XHR && XHR.prototype) {
    var originalOpen = XHR.prototype.open;
    var originalSend = XHR.prototype.send;

    XHR.prototype.open = function (method, url) {
      this.__suppsCartUrl = url;
      return originalOpen.apply(this, arguments);
    };

    XHR.prototype.send = function (body) {
      var self = this;
      var url = self.__suppsCartUrl;

      if (isAddUrl(url) && body) {
        var parsed = readAddBody(body);
        if (parsed && bundleTarget(parsed.data)) {
          self.addEventListener('load', function () {
            if (self.status < 200 || self.status >= 300) return;
            cartHasBag = true; // optimistic; the reconcile below corrects it
            cartAdd(FREE_BAG_VARIANT_ID, 1)
              .then(function (res) {
                applySections(res && res.sections);
              })
              .catch(function () {
                cartHasBag = false;
              })
              .then(function () {
                setTimeout(reconcile, 0);
              });
          });
          return originalSend.apply(self, arguments);
        }
      }

      if (!reconciling && isCartMutationUrl(url)) {
        self.addEventListener('load', function () {
          if (self.status >= 200 && self.status < 300) setTimeout(reconcile, 0);
        });
      }

      return originalSend.apply(this, arguments);
    };
  }

  /* Does THIS selection earn the bag, asked before anything is in the cart?
     cartQualifies() above can only answer for a cart that already exists, which
     is no use to a caller that has to decide what to put IN the cart. Same two
     conditions, same constants, exposed so nothing has to restate the rule. The
     format-rejector buy box posts product and bag in ONE /cart/add so there is
     no window where the gift can go missing, and it asks this to know whether
     to include the second line. */
  function selectionQualifies(variantId, sellingPlanId) {
    return QUALIFYING_VARIANT_IDS.indexOf(Number(variantId)) !== -1 && !!sellingPlanId;
  }

  window.SuppsFreeBag = {
    reconcile: reconcile,
    selectionQualifies: selectionQualifies,
    freeBagVariantId: FREE_BAG_VARIANT_ID,
    state: function () {
      return { cartHasBag: cartHasBag };
    },
  };

  reconcile();
})();
